import { useState, useRef } from 'react'
import { Logger } from '@/services/Logger'
import { useUserRoles } from '@/contexts/UserRolesContext'
import Navbar from '@/components/Navbar'
import HeaderBar from '@/components/HeaderBar'
import './BulkAllocateSimsPage.css'
import { Helmet } from 'react-helmet-async'
import { PAGES } from '@/constants/pages'
import { createTemplate, parseTemplate, isExcelFileType, convertDataToCsvString } from '@/utils/spreadsheetHelpers'
import { MESSAGES } from '@/constants/messages'
import { BULK_ALLOCATE_SIMS_TEMPLATE } from '@/constants/templateSchema'
import { BulkAllocateSimsResponse, DeviceService } from '@/services/DeviceService'

type CsvValidationResult = {
  isValid: boolean
  errors: string[]
  rowCount: number
}

export default function BulkAllocateSimsPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle')
  const [validationResult, setValidationResult] = useState<CsvValidationResult | null>(null)
  const [uploadError, setUploadError] = useState<string>('')
  const [csvContent, setCsvContent] = useState<string>('')
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'processing' | 'completed' | 'error'>('idle')
  const [processingResults, setProcessingResults] = useState<BulkAllocateSimsResponse | null>(null)
  const [processingError, setProcessingError] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  const { loading: rolesLoading } = useUserRoles()

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await isExcelFileType(file)
    if (!result) {
      setUploadError(MESSAGES.ERRORS.FILE.INVALID_TYPE)
      return
    }

    setSelectedFile(file)
    setUploadError('')
    setValidationResult(null)
    setUploadStatus('idle')

    Logger.info('BulkAllocateSimsPage: Excel file selected', { fileName: file.name, fileSize: file.size })
  }

  const handleDropZoneFiles = async (fileList: FileList | null | undefined) => {
    const file = fileList?.[0]
    if (!file) return
    
    const result = await isExcelFileType(file)
    if (!result) {
      setUploadError(MESSAGES.ERRORS.FILE.INVALID_TYPE)
      return
    }
    
    setSelectedFile(file)
    setUploadError('')
    setValidationResult(null)
    setUploadStatus('idle')
    Logger.info('BulkAllocateSimsPage: Excel file selected (drag-drop)', { fileName: file.name, fileSize: file.size })
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragActive) setIsDragActive(true)
  }

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      handleDropZoneFiles(e.dataTransfer.files)
      e.dataTransfer.clearData()
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setUploadStatus('uploading')
    setUploadError('')

    try {
      const template = await parseTemplate(selectedFile, BULK_ALLOCATE_SIMS_TEMPLATE.columns)
      if (!template.success) {
        setUploadStatus('error')
        setUploadError(template.error)
        return
      }

      const result = await convertDataToCsvString(template.data.headers, template.data.rows)
      setCsvContent(result)
      setUploadStatus('success')

    } catch (error) {
      setUploadStatus('error')
      setUploadError(MESSAGES.ERRORS.FILE.EXCEPTION)
    }
  }

  const downloadTemplate = () => {
    createTemplate(BULK_ALLOCATE_SIMS_TEMPLATE)
  }

  const handleProcessSims = async () => {
    if (!csvContent) return

    setProcessingStatus('processing')
    setProcessingError('')
    setProcessingResults(null)

    try {
      Logger.info('BulkAllocateSimsPage: Starting SIM processing')
      const results = await DeviceService.bulkAllocateSims(csvContent)
      setProcessingResults(results)

      if (results.success) {
        setProcessingStatus('completed')
        Logger.info('BulkAllocateSimsPage: SIM processing completed successfully', {
          totalRows: results.totalRows
        })
      } else {
        setProcessingStatus('error')
        setProcessingError(results.errorMessage || 'An error occurred while processing SIMs')
        Logger.warn('BulkAllocateSimsPage: SIM processing failed', {
          errorMessage: results.errorMessage,
          failedRow: results.failedRow,
          totalRows: results.totalRows
        })
      }
    } catch (error) {
      setProcessingStatus('error')
      setProcessingError((error as Error).message || 'An error occurred while processing SIMs')
      Logger.error('BulkAllocateSimsPage: SIM processing exception', { error: (error as Error).message })
    }
  }

  const resetUpload = () => {
    setSelectedFile(null)
    setUploadStatus('idle')
    setValidationResult(null)
    setUploadError('')
    setCsvContent('')
    setProcessingStatus('idle')
    setProcessingResults(null)
    setProcessingError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (rolesLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className="page-container">
      <Helmet>
        <title>{PAGES.BULK_UPLOAD_SIMS.title}</title>
      </Helmet>
      <Navbar />
      <main className="page-main-content">
        <HeaderBar />
        <div className="page-content">
          <h2>Bulk Allocate SIMs</h2>

          <div className="bulk-upload-container">
            <div className="upload-description">
              <p>
                Bulk upload SIMs to existing devices using an Excel file containing values required for processing.
                You can download an example{' '}
                <button
                  className="template-link"
                  onClick={downloadTemplate}
                >
                  Excel file here
                </button>.
              </p>
            </div>

            <div
              className={`upload-section${isDragActive ? ' drag-active' : ''}`}
              onDragOver={onDragOver}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              {uploadStatus === 'idle' || uploadStatus === 'error' ? (
                <>
                  <div className="file-input-container">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileSelect}
                      className="file-input"
                      id="csv-file-input"
                    />
                    <label htmlFor="csv-file-input" className="file-input-label">
                      Choose File
                    </label>
                    <div className="drop-hint">or drag and drop an Excel file here</div>
                    {selectedFile && (
                      <span className="selected-file-name">{selectedFile.name}</span>
                    )}
                  </div>

                  {uploadError && (
                    <div className="error-message">
                      {uploadError}
                    </div>
                  )}

                  <button
                    className="upload-btn"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    Upload
                  </button>
                </>
              ) : uploadStatus === 'uploading' ? (
                <div className="upload-progress">
                  <div className="spinner"></div>
                  <p>Processing CSV file...</p>
                </div>
              ) : uploadStatus === 'success' ? (
                <div className="upload-success">
                  {processingStatus === 'idle' ? (
                    <>
                      <h3>CSV File Validated Successfully</h3>
                      <p>Your CSV file has been validated and contains {validationResult?.rowCount} rows of data.</p>
                      <div className="success-actions">
                        <button className="reset-btn" onClick={resetUpload}>
                          Back
                        </button>
                        <button className="process-btn" onClick={handleProcessSims}>
                          Process SIMs
                        </button>
                      </div>
                    </>
                  ) : processingStatus === 'processing' ? (
                    <div className="processing-section">
                      <div className="spinner"></div>
                      <h3>Processing SIM Allocations</h3>
                      <p>Updating devices with SIM information from the CSV file...</p>
                    </div>
                  ) : processingStatus === 'completed' ? (
                    <div className="processing-results">
                      <h3>SIM Processing Complete</h3>
                      <p>Successfully processed all {processingResults?.totalRows || 0} rows from the CSV file.</p>
                      <p>All devices have been updated with their SIM information.</p>

                      <div className="success-actions">
                        <button className="reset-btn" onClick={resetUpload}>
                          Process Another File
                        </button>
                      </div>
                    </div>
                  ) : processingStatus === 'error' ? (
                    <div className="processing-error">
                      <h3>Upload Unsuccessful</h3>
                      <p>The upload failed and no changes were made to any devices.</p>
                      <div className="error-message">
                        {processingError}
                        {processingResults && processingResults.failedRow > 0 && (
                          <div className="failed-row-info">
                            Failed at row {processingResults.failedRow} of {processingResults.totalRows}
                          </div>
                        )}
                      </div>
                      <div className="success-actions">
                        <button className="reset-btn" onClick={resetUpload}>
                          Try Again
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}